import { useQuery } from "@tanstack/react-query";
import DealerGroupingClient from "../../clients/DealerGroupingClient";

/**
 * Resolves brand from a dealer group row (commonId suffix, then brand, then salesCode).
 * @param {object} dealer
 * @returns {"F"|"L"|null}
 */
export const getDealerGroupRowBrand = (dealer) => {
  if (!dealer) {
    return null;
  }

  const commonId = dealer.commonId;
  if (typeof commonId === "string" && commonId.length > 0) {
    if (commonId.endsWith("F")) {
      return "F";
    }
    if (commonId.endsWith("L")) {
      return "L";
    }
  }

  if (dealer.brand === "F" || dealer.brand === "L") {
    return dealer.brand;
  }

  const salesCode = dealer.salesCode;
  if (typeof salesCode === "string" && salesCode.length > 0) {
    const prefix = salesCode.charAt(0).toUpperCase();
    if (prefix === "F") {
      return "F";
    }
    if (prefix === "L") {
      return "L";
    }
  }

  return null;
};

/**
 * Dual = at least one Ford and at least one Lincoln in the group.
 * Standalone = only Ford or only Lincoln (at least one row of that brand).
 * @param {Array|undefined|null} dealerGroups
 * @returns {"Standalone"|"Dual"|null}
 */
export const getDealerGroupType = (dealerGroups) => {
  if (!dealerGroups || !Array.isArray(dealerGroups)) {
    return null;
  }

  if (dealerGroups.length === 0) {
    return null;
  }

  let fordCount = 0;
  let lincolnCount = 0;

  dealerGroups.forEach((dealer) => {
    const brand = getDealerGroupRowBrand(dealer);
    if (brand === "F") {
      fordCount += 1;
    } else if (brand === "L") {
      lincolnCount += 1;
    }
  });

  if (fordCount >= 1 && lincolnCount >= 1) {
    return "Dual";
  }

  if (fordCount >= 1 && lincolnCount === 0) {
    return "Standalone";
  }

  if (lincolnCount >= 1 && fordCount === 0) {
    return "Standalone";
  }

  // Single row with no detectable brand — treat as standalone for Stripe metadata
  if (dealerGroups.length === 1) {
    return "Standalone";
  }

  return null;
};

/**
 * Custom hook to get dealer group count and return a string representation
 * @param {Object} user - User object required for the API call
 * @param {string} salesCode - Sales code parameter
 * @param {string} paCode - PA code parameter
 * @returns {Object} Hook result with dealerGroupType, isLoading, isError, error, and raw data
 */
export const useDealerGroupCount = (user, salesCode, paCode) => {
  const queryResult = useQuery({
    queryKey: ["dealerGroup", salesCode, paCode],
    queryFn: () =>
      DealerGroupingClient.getDealerGroup(user, salesCode, null, false, paCode),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const { data, isLoading, isError, error } = queryResult;

  return {
    dealerGroupType: getDealerGroupType(data),
    dealerGroupCount: data?.length || 0,
    isLoading,
    isError,
    error,
    data, // Raw dealer group data in case needed
  };
};

export default useDealerGroupCount;
